import React, { } from "react";
import { Form, Button, Divider, Typography } from "antd";
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  LockOutlined,
} from "@ant-design/icons";
import TextInput from "../ReusableInputs/TextInput";
import NumberInput from "../ReusableInputs/NumberInput";
import { Link } from "react-router-dom";
import "./style.css";

const {  Text } = Typography;

// Loading component
const Loader = () => (
  <div className="flex items-center justify-center">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#75A7AD]"></div>
  </div>
);

interface SignupFormProps {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phone_number: number;
  setFirstName: (value: string) => void;
  setLastName: (value: string) => void;
  setEmail: (value: string) => void;
  setPassword: (value: string) => void;
  setPhoneNumber: (value: number) => void;
  onFinish: () => void;
  loading: boolean;
}

const SignupForm: React.FC<SignupFormProps> = ({
  lastName,
  setLastName,
  firstName,
  setFirstName,
  email,
  setEmail,
  password,
  setPassword,
  onFinish,
  loading,
  phone_number,
  setPhoneNumber,
}) => {
  const [form] = Form.useForm();

  const validatePassword = (_: any, value: string) => {
    if (!value) {
      return Promise.reject(new Error("Please input your password"));
    }
    if (value.length < 8) {
      return Promise.reject(
        new Error("Password must be at least 8 characters long")
      );
    }
    if (!/[A-Z]/.test(value)) {
      return Promise.reject(
        new Error("Password must contain at least one uppercase letter")
      );
    }
    if (!/[\W_]/.test(value)) {
      return Promise.reject(
        new Error("Password must contain at least one special character")
      );
    }
    if (value.includes(" ")) {
      return Promise.reject(new Error("Password cannot contain spaces"));
    }
    return Promise.resolve();
  };

  // Add this validation function near your other validation functions
  const validateEmail = (_: any, value: string) => {
    if (!value) {
      return Promise.reject(new Error("Please input your email"));
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return Promise.reject(new Error("Please enter a valid email address"));
    }
    if (
      value.toLowerCase().endsWith("@yahoo.com") ||
      value.toLowerCase().endsWith("@yahoo.co.uk") ||
      value.toLowerCase().endsWith("@ymail.com")
    ) {
      return Promise.reject(
        new Error(
          "Yahoo email addresses are currently not supported. Kindly use an alternative email address to sign up."
        )
      );
    }
    return Promise.resolve();
  };

  // Then update the email TextInput component to use this validator:

  return (
    <div className="relative w-full mt-[-30%] mb-[0px] lg:mb-[10px] p-4  opacity-80">
      <section className="flex items-center justify-center w-full py-10">
        <div className="bg-white rounded-xl shadow-lg border border-[#75A7AD]/20 w-full  sm:max-w-[55%] p-8 ">
          <div className="text-center mb-6">
            {/* <Title level={2} className="text-[#005D6E] m-0">Create Account</Title>
          <Text className="text-gray-500">Join us and start your journey</Text> */}
          </div>

          <Form form={form} onFinish={onFinish} layout="vertical">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextInput
                title="First Name"
                placeholder="Enter first name"
                inputValue={firstName}
                setInputValue={setFirstName}
                name="firstName"
                required={true}
                prefix={<UserOutlined className="text-[#75A7AD] mr-2" />}
              />

              <TextInput
                title="Last Name"
                placeholder="Enter last name"
                inputValue={lastName}
                setInputValue={setLastName}
                name="lastName"
                required={true}
                prefix={<UserOutlined className="text-[#75A7AD] mr-2" />}
              />
            </div>

            <TextInput
              type="email"
              title="Email Address"
              placeholder="Enter your email"
              inputValue={email}
              setInputValue={setEmail}
              name="email"
              required={true}
              prefix={<MailOutlined className="text-[#75A7AD] mr-2" />}
              tooltipText="We'll never share your email with anyone else"
              rules={[
                {
                  validator: validateEmail,
                },
              ]}
            />
            <NumberInput
              inputValue={phone_number}
              setInputValue={setPhoneNumber}
              title="Phone Number"
              placeholder="Enter your phone number"
              required={true}
              prefix={<PhoneOutlined className="text-[#75A7AD] mr-2" />}
              tooltipText="We'll use this for account verification"
              rules={[
                {
                  required: true,
                  message: "Phone number is required",
                },
              ]}
              name="phoneNumber"
            />

            <TextInput
              type="password"
              title="Password"
              placeholder="Create a strong password"
              inputValue={password}
              setInputValue={setPassword}
              name="password"
              required={true}
              prefix={<LockOutlined className="text-[#75A7AD] mr-2" />}
              tooltipText="Must include uppercase, special character, and be at least 8 characters"
              rules={[{ validator: validatePassword }]}
            />

            <div className="mt-6">
              <Button
                htmlType="submit"
                className="custom-button w-full h-12 text-white font-medium rounded-full bg-gradient-to-r from-[#75A7AD] to-[#005D6E] hover:opacity-90 transition-all duration-300 flex items-center justify-center"
                disabled={loading}>
                {loading ? <Loader /> : "Create Account"}
              </Button>
            </div>

            <Divider plain>
              <span className="text-gray-400 text-sm">or</span>
            </Divider>

            <div className="text-center mt-4">
              <Link to="/signin">
                <Text className="text-[#005D6E] hover:text-[#75A7AD] transition-colors duration-300">
                  Already have an account?{" "}
                  <span className="font-semibold">Sign in</span>
                </Text>
              </Link>
            </div>
          </Form>
        </div>
      </section>
    </div>
  );
};

export default SignupForm;
